import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { BenachrichtigungenApi } from '@elbklar/ui';

import { sitzungErneuern, sitzungsStand, zurAnmeldung } from '@/features/auth/sitzung';
import type {
  BalanceSnapshot,
  Category,
  CategoryRule,
  CategoryRuleUpdate,
  CategoryUpdate,
  ColumnMapping,
  DraftImpacts,
  FinanceSettings,
  ImportCommitResult,
  ImportPreview,
  ImportRun,
  Loan,
  LoanSchedule,
  Overview,
  PlannedItem,
  Projection,
  RadarActor,
  RadarActorStatus,
  RadarActorType,
  RadarChange,
  RadarDeadlineKind,
  RadarEffort,
  RadarKind,
  RadarProgram,
  RadarProgramFilters,
  RadarRewardKind,
  RadarRun,
  RadarSettings,
  RadarStatus,
  RadarSummary,
  RecurringItem,
  RuleApplyResult,
  Transaction,
  TransactionFilters,
  TransactionList,
  TransactionSummary,
} from '@/types';

// Finance-API laeuft auf Port 8003; im Dev-Betrieb proxyt Vite /api dorthin.
const API_URL = import.meta.env.VITE_API_URL || '/api';
const ACCESS_TOKEN_KEY = 'finance_access_token';

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // Cookie __elbklar_access — SSO mit CRM und PM
});

// Anfrage mit Vermerk für die Erneuerung der Sitzung (siehe `bei401`).
type MitSitzung = InternalAxiosRequestConfig & { _sitzungsStand?: number; _wiederholt?: boolean };

api.interceptors.request.use((config: MitSitzung) => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Auf welchem Stand der Sitzung die Anfrage losging (siehe `bei401`).
  config._sitzungsStand = sitzungsStand();
  return config;
});

/**
 * 401: Meist ist nur das Access-Cookie abgelaufen (15 min), die Sitzung gilt
 * per Refresh noch. Deshalb erst erneuern und die Anfrage EINMAL wiederholen;
 * zur Anmeldung nur, wenn core die Erneuerung ablehnt oder die Wiederholung
 * erneut 401 bekommt. Gleichzeitige 401er teilen sich einen Refresh
 * (`sitzungErneuern`). Wie in crm-web und pm-web; der Refresh läuft über
 * `fetch` an core-api, nie über diesen Client, und kann nicht hier landen.
 */
async function bei401(error: AxiosError) {
  // Ein altes Bearer-Token aus dem localStorage verdeckte sonst bei der
  // Wiederholung das frische Cookie.
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  const config = error.config as MitSitzung | undefined;
  if (!config || config._wiederholt) {
    zurAnmeldung();
    throw error;
  }
  // Seit dem Absenden schon erneuert (die 401 kam nach dem Refresh an):
  // nicht noch einmal erneuern, nur wiederholen.
  const ergebnis = config._sitzungsStand !== sitzungsStand() ? 'erneuert' : await sitzungErneuern();
  if (ergebnis === 'erneuert') {
    config._wiederholt = true;
    delete config.headers?.Authorization;
    return api.request(config);
  }
  if (ergebnis === 'abgelehnt') zurAnmeldung();
  // `nicht_erreichbar`: Netz oder core gestört. Die Anmeldeseite hälfe nicht
  // und nähme die Eingaben mit; der Fehler geht an den Aufrufer.
  throw error;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return bei401(error);
    }
    return Promise.reject(error);
  }
);

export { api, ACCESS_TOKEN_KEY };

// =============================================================================
// Auswertung
// =============================================================================

export const overviewApi = {
  get: async (): Promise<Overview> => (await api.get('/overview')).data,
};

export const projectionApi = {
  get: async (params?: {
    months?: number;
    scenario_ids?: string[];
  }): Promise<Projection> => {
    const query: Record<string, string | number> = {};
    if (params?.months) query.months = params.months;
    if (params?.scenario_ids?.length) {
      query.scenario_ids = params.scenario_ids.join(',');
    }
    return (await api.get('/projection', { params: query })).data;
  },

  /** Einzelwirkung jedes Entwurfs — untereinander vergleichbar, nicht addierbar. */
  impacts: async (params?: { months?: number }): Promise<DraftImpacts> => {
    const query: Record<string, string | number> = {};
    if (params?.months) query.months = params.months;
    return (await api.get('/projection/impacts', { params: query })).data;
  },
};

// =============================================================================
// Buchungen
// =============================================================================

export const transactionsApi = {
  list: async (filters: TransactionFilters = {}): Promise<TransactionList> =>
    (await api.get('/transactions', { params: filters })).data,

  summary: async (filters: TransactionFilters = {}): Promise<TransactionSummary> =>
    (await api.get('/transactions/summary', { params: filters })).data,

  create: async (payload: {
    booking_date: string;
    amount: string;
    counterparty?: string | null;
    purpose?: string | null;
    category_id?: string | null;
    tax_rate?: string;
    notes?: string | null;
  }): Promise<Transaction> => (await api.post('/transactions', payload)).data,

  update: async (
    id: string,
    payload: Partial<{
      booking_date: string;
      amount: string;
      counterparty: string | null;
      purpose: string | null;
      category_id: string | null;
      tax_rate: string;
      notes: string | null;
    }>
  ): Promise<Transaction> => (await api.patch(`/transactions/${id}`, payload)).data,

  remove: async (id: string): Promise<void> => {
    await api.delete(`/transactions/${id}`);
  },
};

// =============================================================================
// Kategorien und Regeln
// =============================================================================

export const categoriesApi = {
  list: async (includeArchived = false): Promise<Category[]> =>
    (await api.get('/categories', { params: { include_archived: includeArchived } }))
      .data,

  create: async (payload: {
    name: string;
    kind?: string;
    color?: string | null;
  }): Promise<Category> => (await api.post('/categories', payload)).data,

  update: async (id: string, payload: CategoryUpdate): Promise<Category> =>
    (await api.patch(`/categories/${id}`, payload)).data,

  remove: async (id: string): Promise<void> => {
    await api.delete(`/categories/${id}`);
  },
};

export const categoryRulesApi = {
  list: async (): Promise<CategoryRule[]> => (await api.get('/category-rules')).data,

  create: async (payload: {
    pattern: string;
    field: string;
    category_id: string;
    priority?: number;
  }): Promise<CategoryRule> => (await api.post('/category-rules', payload)).data,

  update: async (id: string, payload: CategoryRuleUpdate): Promise<CategoryRule> =>
    (await api.patch(`/category-rules/${id}`, payload)).data,

  remove: async (id: string): Promise<void> => {
    await api.delete(`/category-rules/${id}`);
  },

  apply: async (): Promise<RuleApplyResult> =>
    (await api.post('/category-rules/apply')).data,
};

// =============================================================================
// Fixkosten
// =============================================================================

export const recurringApi = {
  list: async (includeInactive = false): Promise<RecurringItem[]> =>
    (await api.get('/recurring', { params: { include_inactive: includeInactive } }))
      .data,

  create: async (payload: {
    name: string;
    amount: string;
    category_id?: string | null;
    tax_rate?: string;
    interval?: string;
    day_of_month?: number;
    start_date: string;
    end_date?: string | null;
    notes?: string | null;
  }): Promise<RecurringItem> => (await api.post('/recurring', payload)).data,

  update: async (
    id: string,
    payload: Partial<{
      name: string;
      amount: string;
      category_id: string | null;
      tax_rate: string;
      interval: string;
      day_of_month: number;
      start_date: string;
      end_date: string | null;
      is_active: boolean;
      notes: string | null;
    }>
  ): Promise<RecurringItem> => (await api.patch(`/recurring/${id}`, payload)).data,

  remove: async (id: string): Promise<void> => {
    await api.delete(`/recurring/${id}`);
  },
};

// =============================================================================
// Planung
// =============================================================================

export const plannedApi = {
  list: async (status?: string): Promise<PlannedItem[]> =>
    (await api.get('/planned', { params: status ? { status_filter: status } : {} }))
      .data,

  create: async (payload: {
    name: string;
    amount: string;
    due_date: string;
    category_id?: string | null;
    tax_rate?: string;
    repeat_interval?: string | null;
    repeat_count?: number | null;
    notes?: string | null;
  }): Promise<PlannedItem> => (await api.post('/planned', payload)).data,

  update: async (
    id: string,
    payload: Partial<{
      name: string;
      amount: string;
      due_date: string;
      category_id: string | null;
      tax_rate: string;
      repeat_interval: string | null;
      repeat_count: number | null;
      notes: string | null;
    }>
  ): Promise<PlannedItem> => (await api.patch(`/planned/${id}`, payload)).data,

  commit: async (id: string): Promise<PlannedItem> =>
    (await api.post(`/planned/${id}/commit`)).data,

  uncommit: async (id: string): Promise<PlannedItem> =>
    (await api.post(`/planned/${id}/uncommit`)).data,

  remove: async (id: string): Promise<void> => {
    await api.delete(`/planned/${id}`);
  },
};

// =============================================================================
// Kredit
// =============================================================================

export const loansApi = {
  list: async (): Promise<Loan[]> => (await api.get('/loans')).data,

  get: async (id: string): Promise<Loan> => (await api.get(`/loans/${id}`)).data,

  create: async (payload: {
    name: string;
    principal: string;
    interest_rate: string;
    term_months: number;
    monthly_payment: string;
    disbursement_date: string;
    first_payment_date: string;
    grace_months?: number;
    notes?: string | null;
  }): Promise<Loan> => (await api.post('/loans', payload)).data,

  update: async (
    id: string,
    payload: Partial<{
      name: string;
      principal: string;
      interest_rate: string;
      term_months: number;
      monthly_payment: string;
      disbursement_date: string;
      first_payment_date: string;
      grace_months: number;
      is_active: boolean;
      notes: string | null;
    }>
  ): Promise<Loan> => (await api.patch(`/loans/${id}`, payload)).data,

  remove: async (id: string): Promise<void> => {
    await api.delete(`/loans/${id}`);
  },

  schedule: async (id: string): Promise<LoanSchedule> =>
    (await api.get(`/loans/${id}/schedule`)).data,
};

// =============================================================================
// Kontostand
// =============================================================================

export const balanceApi = {
  list: async (): Promise<BalanceSnapshot[]> => (await api.get('/balance')).data,

  current: async (): Promise<BalanceSnapshot | null> =>
    (await api.get('/balance/current')).data,

  create: async (payload: {
    as_of: string;
    amount: string;
    note?: string | null;
  }): Promise<BalanceSnapshot> => (await api.post('/balance', payload)).data,
};

// =============================================================================
// Import
// =============================================================================

export const importsApi = {
  history: async (): Promise<ImportRun[]> => (await api.get('/imports')).data,

  preview: async (file: File): Promise<ImportPreview> => {
    const form = new FormData();
    form.append('file', file);
    return (
      await api.post('/imports/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data;
  },

  commit: async (
    file: File,
    mapping: ColumnMapping,
    balance?: { as_of?: string; amount?: string }
  ): Promise<ImportCommitResult> => {
    const form = new FormData();
    form.append('file', file);
    form.append('mapping', JSON.stringify(mapping));
    if (balance?.as_of) form.append('balance_as_of', balance.as_of);
    if (balance?.amount) form.append('balance_amount', balance.amount);

    return (
      await api.post('/imports/commit', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data;
  },
};


// =============================================================================
// Einstellungen
// =============================================================================

export const settingsApi = {
  get: async (): Promise<FinanceSettings> => (await api.get('/settings')).data,

  update: async (payload: {
    min_buffer?: string;
    horizon_months?: number;
    burn_lookback_months?: number;
    vat_enabled?: boolean;
    vat_period_months?: number;
    vat_delay_months?: number;
  }): Promise<FinanceSettings> => (await api.put('/settings', payload)).data,
};

// =============================================================================
// Förderradar
// =============================================================================

export const radarApi = {
  summary: async (): Promise<RadarSummary> => (await api.get('/radar/summary')).data,

  programs: async (filters: RadarProgramFilters = {}): Promise<RadarProgram[]> => {
    const params: Record<string, string | boolean> = {};
    if (filters.kind) params.kind = filters.kind;
    if (filters.status) params.status = filters.status;
    if (filters.source_state) params.source_state = filters.source_state;
    if (filters.suche?.trim()) params.suche = filters.suche.trim();
    if (filters.include_closed) params.include_closed = true;
    if (filters.sort) params.sort = filters.sort;
    return (await api.get('/radar/programs', { params })).data;
  },

  program: async (id: string): Promise<RadarProgram> =>
    (await api.get(`/radar/programs/${encodeURIComponent(id)}`)).data,

  createProgram: async (payload: {
    kind: RadarKind;
    title: string;
    provider?: string | null;
    source_url?: string | null;
    summary?: string | null;
    deadline?: string | null;
    deadline_kind?: RadarDeadlineKind;
    event_date?: string | null;
    event_end_date?: string | null;
    location?: string | null;
  }): Promise<RadarProgram> => (await api.post('/radar/programs', payload)).data,

  updateProgram: async (
    id: string,
    payload: Partial<{
      title: string;
      provider: string | null;
      subkind: string | null;
      summary: string | null;
      target_group: string | null;
      amount_min: string | null;
      amount_max: string | null;
      funding_rate: string | null;
      reward_kind: RadarRewardKind | null;
      application_path: string | null;
      prerequisites: string | null;
      combinable_with: string | null;
      deadline: string | null;
      deadline_kind: RadarDeadlineKind;
      event_date: string | null;
      event_end_date: string | null;
      location: string | null;
      score: number | null;
      estimated_effort: RadarEffort | null;
      review_at: string | null;
      status: RadarStatus;
      note: string | null;
      source_url: string | null;
      reference_url: string | null;
      manual_search_query: string | null;
    }>
  ): Promise<RadarProgram> => (await api.patch(`/radar/programs/${encodeURIComponent(id)}`, payload)).data,

  removeProgram: async (id: string): Promise<void> => {
    await api.delete(`/radar/programs/${encodeURIComponent(id)}`);
  },

  /** Der Mensch hat die Quelle händisch angesehen. */
  checked: async (
    id: string,
    payload: { changed: boolean; note?: string | null }
  ): Promise<RadarProgram> =>
    (await api.post(`/radar/programs/${encodeURIComponent(id)}/checked`, payload)).data,

  changes: async (id: string): Promise<RadarChange[]> =>
    (await api.get(`/radar/programs/${encodeURIComponent(id)}/changes`)).data,

  /** „In Planung übernehmen" — erwarteter Zufluss als geplante Einnahme (Entwurf). */
  planProgram: async (
    id: string,
    // Decimal-Text aus dem Betragsfeld (`RadarPlanRequest.amount` ist Decimal).
    payload: { amount: string; due_date: string; notes?: string }
  ): Promise<RadarProgram> => (await api.post(`/radar/programs/${encodeURIComponent(id)}/plan`, payload)).data,

  actors: async (filters: {
    status?: RadarActorStatus;
    type?: RadarActorType;
  } = {}): Promise<RadarActor[]> => (await api.get('/radar/actors', { params: filters })).data,

  createActor: async (payload: {
    name: string;
    type: RadarActorType;
    region?: string | null;
    url?: string | null;
    programs_url?: string | null;
    note?: string | null;
    status?: RadarActorStatus;
  }): Promise<RadarActor> => (await api.post('/radar/actors', payload)).data,

  updateActor: async (
    id: string,
    payload: Partial<{
      name: string;
      type: RadarActorType;
      region: string | null;
      url: string | null;
      programs_url: string | null;
      note: string | null;
      status: RadarActorStatus;
    }>
  ): Promise<RadarActor> => (await api.patch(`/radar/actors/${encodeURIComponent(id)}`, payload)).data,

  removeActor: async (id: string): Promise<void> => {
    await api.delete(`/radar/actors/${encodeURIComponent(id)}`);
  },

  settings: async (): Promise<RadarSettings> => (await api.get('/radar/settings')).data,

  updateSettings: async (
    payload: Partial<Omit<RadarSettings, 'id' | 'last_monitor_at' | 'last_discovery_at'>>
  ): Promise<RadarSettings> => (await api.put('/radar/settings', payload)).data,

  runs: async (limit = 20): Promise<RadarRun[]> =>
    (await api.get('/radar/runs', { params: { limit } })).data,

  startRun: async (
    kind: 'monitor' | 'discovery' = 'monitor'
  ): Promise<{ kind: string; job_id: string | null; status: string }> =>
    (await api.post('/radar/runs', { kind })).data,
};

// =============================================================================
// Benachrichtigungen (Modul aus @elbklar/ui)
// =============================================================================

/**
 * Adapter für `BenachrichtigungenProvider`: dieselbe axios-Instanz wie alle
 * übrigen Aufrufe (Basis /api, Cookie, 401 zur Anmeldung). finance-api bindet
 * die Router aus core seit MR N unter `/notifications` ein.
 */
export const benachrichtigungenApi: BenachrichtigungenApi = {
  get: async <T,>(pfad: string, params?: Record<string, string | number>) => (await api.get<T>(pfad, { params })).data,
  post: async <T,>(pfad: string, daten?: unknown) => (await api.post<T>(pfad, daten)).data,
  put: async <T,>(pfad: string, daten: unknown) => (await api.put<T>(pfad, daten)).data,
};
